/**
 * AuditDashboard - Real-time audit performance dashboard
 * 
 * KPI cards, compliance trends, overdue actions, risk heatmap,
 * recent activity feed, and audit coverage stats.
 */

import React, { useState, useEffect } from 'react';
import {
  ClipboardCheck, AlertTriangle, CheckCircle2, Clock, TrendingUp,
  TrendingDown, Eye, BarChart3, ShieldCheck,
  Target, Users, Calendar, CalendarDays, FileWarning,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@shared/components/ui/card';
import { Badge } from '@shared/components/ui/badge';
import { Progress } from '@shared/components/ui/progress';
import { supabase } from '@shared/supabaseClient';

// ─── KPI Card ───────────────────────────────────────────────────────

function KPICard({ title, value, subtitle, icon: Icon, trend, trendLabel, color = 'amber', className = '' }) {
  const colorMap = {
    amber:  'from-amber-500/10 to-amber-600/5 border-amber-500/20',
    green:  'from-green-500/10 to-green-600/5 border-green-500/20',
    red:    'from-red-500/10 to-red-600/5 border-red-500/20',
    blue:   'from-blue-500/10 to-blue-600/5 border-blue-500/20',
    purple: 'from-purple-500/10 to-purple-600/5 border-purple-500/20',
  };

  const iconColorMap = {
    amber: 'text-amber-500', green: 'text-green-500', red: 'text-red-500',
    blue: 'text-blue-500', purple: 'text-purple-500',
  };

  return (
    <Card className={`bg-gradient-to-br ${colorMap[color]} border ${className} overflow-hidden`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-3xl font-bold text-foreground">{value}</p>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          <div className={`p-2.5 rounded-lg bg-background/60 ${iconColorMap[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        {trend !== undefined && (
          <div className="mt-3 flex items-center gap-1 text-xs">
            {trend >= 0 ? (
              <TrendingUp className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <TrendingDown className="h-3.5 w-3.5 text-red-500" />
            )}
            <span className={trend >= 0 ? 'text-green-600' : 'text-red-600'}>
              {trend > 0 ? '+' : ''}{trend}%
            </span>
            <span className="text-muted-foreground ml-1">{trendLabel || 'vs last month'}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Risk Heatmap ───────────────────────────────────────────────────
function RiskHeatmap({ findings }) {
  const matrix = {
    // likelihood → consequence → count
    rare:           { insignificant: 0, minor: 0, moderate: 0, major: 0, catastrophic: 0 },
    unlikely:       { insignificant: 0, minor: 0, moderate: 0, major: 0, catastrophic: 0 },
    possible:       { insignificant: 0, minor: 0, moderate: 0, major: 0, catastrophic: 0 },
    likely:         { insignificant: 0, minor: 0, moderate: 0, major: 0, catastrophic: 0 },
    almost_certain: { insignificant: 0, minor: 0, moderate: 0, major: 0, catastrophic: 0 },
  };

  findings?.forEach(f => {
    if (f.likelihood && f.consequence && matrix[f.likelihood]) {
      matrix[f.likelihood][f.consequence]++;
    }
  });

  const riskColor = (l, c) => {
    const lIdx = ['rare', 'unlikely', 'possible', 'likely', 'almost_certain'].indexOf(l);
    const cIdx = ['insignificant', 'minor', 'moderate', 'major', 'catastrophic'].indexOf(c);
    const score = (lIdx + 1) * (cIdx + 1);
    if (score >= 15) return 'bg-red-600 text-white';
    if (score >= 10) return 'bg-orange-500 text-white';
    if (score >= 5)  return 'bg-amber-400 text-black';
    return 'bg-green-400 text-black';
  };

  const consequences = ['insignificant', 'minor', 'moderate', 'major', 'catastrophic'];
  const likelihoods = ['almost_certain', 'likely', 'possible', 'unlikely', 'rare'];
  const likeLabels = { almost_certain: 'Almost Certain', likely: 'Likely', possible: 'Possible', unlikely: 'Unlikely', rare: 'Rare' };
  const consLabels = { insignificant: 'Insignif.', minor: 'Minor', moderate: 'Moderate', major: 'Major', catastrophic: 'Catast.' };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-amber-500" />
          Risk Heatmap
        </CardTitle>
        <CardDescription>Likelihood × Consequence matrix</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="p-1 text-left text-muted-foreground w-24">Likelihood</th>
                {consequences.map(c => (
                  <th key={c} className="p-1 text-center text-muted-foreground">{consLabels[c]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {likelihoods.map(l => (
                <tr key={l}>
                  <td className="p-1 font-medium text-muted-foreground text-xs">{likeLabels[l]}</td>
                  {consequences.map(c => (
                    <td key={c} className="p-1">
                      <div className={`w-full h-8 rounded flex items-center justify-center font-bold text-xs ${riskColor(l, c)}`}>
                        {matrix[l][c] || ''}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Compliance Score Gauge ─────────────────────────────────────────
function ComplianceGauge({ score, label = 'Overall Compliance' }) {
  const displayScore = Math.round(score || 0);
  const gaugeColor = displayScore >= 85 ? 'text-green-500' : displayScore >= 70 ? 'text-amber-500' : 'text-red-500';
  const progressColor = displayScore >= 85 ? '[&>div]:bg-green-500' : displayScore >= 70 ? '[&>div]:bg-amber-500' : '[&>div]:bg-red-500';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Target className="h-4 w-4 text-amber-500" />
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4">
        <div className="relative w-32 h-32 flex items-center justify-center">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="54" fill="none" stroke="currentColor" strokeWidth="8" className="text-muted/20" />
            <circle
              cx="60" cy="60" r="54" fill="none"
              stroke="currentColor" strokeWidth="8"
              strokeDasharray={`${(displayScore / 100) * 339.3} 339.3`}
              strokeLinecap="round"
              className={gaugeColor}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-3xl font-bold ${gaugeColor}`}>{displayScore}%</span>
            <span className="text-xs text-muted-foreground">Score</span>
          </div>
        </div>
        <div className="w-full space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0%</span>
            <span>Target: 85%</span>
            <span>100%</span>
          </div>
          <Progress value={displayScore} className={`h-2 ${progressColor}`} />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Finding Status Breakdown ───────────────────────────────────────
function FindingBreakdown({ findings }) {
  const counts = {
    open: 0, action_assigned: 0, in_progress: 0, resolved: 0, verified: 0, closed: 0,
  };
  findings?.forEach(f => { if (counts[f.status] !== undefined) counts[f.status]++; });
  const total = findings?.length || 0;

  const bars = [
    { label: 'Open',     count: counts.open,            color: 'bg-red-500' },
    { label: 'Assigned', count: counts.action_assigned,  color: 'bg-orange-500' },
    { label: 'In Progress', count: counts.in_progress,  color: 'bg-amber-500' },
    { label: 'Resolved', count: counts.resolved,         color: 'bg-blue-500' },
    { label: 'Verified', count: counts.verified,         color: 'bg-green-500' },
    { label: 'Closed',   count: counts.closed,           color: 'bg-gray-500' },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileWarning className="h-4 w-4 text-amber-500" />
          Findings Status
        </CardTitle>
        <CardDescription>{total} total finding{total !== 1 ? 's' : ''}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {bars.map(b => (
          <div key={b.label} className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground w-20 shrink-0">{b.label}</span>
            <div className="flex-1 h-5 bg-muted/30 rounded-full overflow-hidden">
              <div
                className={`h-full ${b.color} rounded-full transition-all duration-500`}
                style={{ width: total ? `${(b.count / total) * 100}%` : '0%' }}
              />
            </div>
            <span className="text-xs font-semibold w-8 text-right">{b.count}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ─── Recent Activity Feed ───────────────────────────────────────────
function ActivityFeed({ activities }) {
  const eventIcons = {
    audit_created: ClipboardCheck,
    audit_submitted: CheckCircle2,
    finding_created: AlertTriangle,
    action_assigned: Users,
    action_completed: CheckCircle2,
    response_updated: Eye,
  };

  const eventColors = {
    audit_created: 'text-blue-500 bg-blue-500/10',
    audit_submitted: 'text-green-500 bg-green-500/10',
    finding_created: 'text-red-500 bg-red-500/10',
    action_assigned: 'text-amber-500 bg-amber-500/10',
    action_completed: 'text-green-500 bg-green-500/10',
    response_updated: 'text-gray-500 bg-gray-500/10',
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-500" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
          {(!activities || activities.length === 0) ? (
            <p className="text-sm text-muted-foreground text-center py-6">No recent activity</p>
          ) : (
            activities.slice(0, 10).map((a, i) => {
              const Icon = eventIcons[a.event_type] || Eye;
              const colorClass = eventColors[a.event_type] || 'text-gray-500 bg-gray-500/10';
              return (
                <div key={a.id || i} className="flex items-start gap-3">
                  <div className={`p-1.5 rounded-md shrink-0 ${colorClass}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground leading-tight">{a.event_description}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {a.user_name} · {new Date(a.created_at).toLocaleDateString('en-AU', {
                        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Upcoming Audits ────────────────────────────────────────────────
function UpcomingAudits({ schedule }) {
  const [horizon, setHorizon] = useState('7d');

  const statusColors = {
    scheduled: 'bg-blue-500/10 text-blue-700 border-blue-500/30',
    overdue: 'bg-red-500/10 text-red-700 border-red-500/30',
    in_progress: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  };

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const filtered = schedule.filter(s => {
    if (horizon === 'all') return true;
    const due = new Date(s.due_date);
    due.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((due - now) / 86400000);
    if (horizon === 'today') return diffDays <= 0;
    if (horizon === '7d') return diffDays <= 7;
    if (horizon === '30d') return diffDays <= 30;
    return true;
  });

  const overdueCount = schedule.filter(s => {
    const due = new Date(s.due_date);
    due.setHours(0, 0, 0, 0);
    return due < now && s.status !== 'completed';
  }).length;

  const horizons = [
    { key: 'today', label: 'Today' },
    { key: '7d', label: '7 Days' },
    { key: '30d', label: '30 Days' },
    { key: 'all', label: 'All' },
  ];

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4 text-amber-500" />
            Upcoming Audits
          </CardTitle>
          {overdueCount > 0 && (
            <Badge variant="destructive" className="text-[10px] font-bold">
              {overdueCount} overdue
            </Badge>
          )}
        </div>
        <div className="flex gap-1 pt-1">
          {horizons.map(h => (
            <button
              key={h.key}
              onClick={() => setHorizon(h.key)}
              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${
                horizon === h.key
                  ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {h.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {filtered.length === 0 ? (
            <div className="text-center py-6">
              <CalendarDays className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">
                {schedule.length === 0 ? 'No scheduled audits' : `No audits due ${horizon === 'today' ? 'today' : horizon === '7d' ? 'in next 7 days' : 'in next 30 days'}`}
              </p>
              {schedule.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">Create an audit programme to get started</p>
              )}
            </div>
          ) : (
            filtered.slice(0, 10).map((s, i) => {
              const due = new Date(s.due_date);
              due.setHours(0, 0, 0, 0);
              const isOverdue = due < now && s.status !== 'completed';
              const displayStatus = isOverdue ? 'overdue' : s.status;

              return (
                <div key={s.id || i} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
                  isOverdue ? 'border-red-200 bg-red-50/50 dark:bg-red-900/10 dark:border-red-800/30' : 'hover:bg-muted/30'
                }`}>
                  <div className="text-center shrink-0 w-12">
                    <div className={`text-lg font-bold leading-none ${isOverdue ? 'text-red-600' : 'text-foreground'}`}>
                      {due.getDate()}
                    </div>
                    <div className="text-[10px] text-muted-foreground uppercase">
                      {due.toLocaleDateString('en-AU', { month: 'short' })}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{s.programme_name || 'Audit'}</p>
                    <p className="text-xs text-muted-foreground truncate">{s.assigned_name || 'Unassigned'}</p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${statusColors[displayStatus] || statusColors.scheduled}`}>
                    {displayStatus === 'overdue' ? 'Overdue' : displayStatus === 'in_progress' ? 'Active' : 'Scheduled'}
                  </Badge>
                </div>
              );
            })
          )}
          {filtered.length > 10 && (
            <p className="text-xs text-center text-muted-foreground pt-1">+{filtered.length - 10} more</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main Dashboard ─────────────────────────────────────────────────
export default function AuditDashboard({ user }) {
  const [stats, setStats] = useState({
    totalAudits: 0, completedThisMonth: 0, overdueFindings: 0, avgScore: 0,
    complianceScore: 0, openFindings: 0,
  });
  const [findings, setFindings] = useState([]);
  const [activities, setActivities] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, [user?.depot_id]);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      // Parallel fetch all dashboard data
      const [
        { count: totalAudits },
        { count: completedThisMonth },
        { data: allFindings },
        { data: recentActivities },
        { data: scores },
        { data: scheduledAudits },
      ] = await Promise.all([
        supabase.from('audits').select('*', { count: 'exact', head: true }),
        supabase.from('audits').select('*', { count: 'exact', head: true })
          .in('status', ['submitted', 'locked']).gte('submitted_at', monthStart),
        supabase.from('audit_findings').select('*').order('identified_at', { ascending: false }).limit(200),
        supabase.from('audit_activity_log').select('*').order('created_at', { ascending: false }).limit(20),
        supabase.from('audits').select('score_percentage').in('status', ['submitted', 'locked']).not('score_percentage', 'is', null),
        supabase.from('audit_schedule').select(`
          *, audit_programmes(programme_name, template_id), users!audit_schedule_assigned_to_fkey(full_name, email)
        `).in('status', ['scheduled', 'overdue', 'in_progress']).order('due_date', { ascending: true }).limit(10),
      ]);

      const avgScore = scores?.length
        ? scores.reduce((sum, s) => sum + (s.score_percentage || 0), 0) / scores.length
        : 0;

      const openFindings = allFindings?.filter(f => 
        ['open', 'action_assigned', 'in_progress'].includes(f.status)
      ).length || 0;

      const overdueFindings = allFindings?.filter(f =>
        f.status === 'open' && f.identified_at &&
        (new Date() - new Date(f.identified_at)) / (1000 * 60 * 60 * 24) > 14
      ).length || 0;

      setStats({
        totalAudits: totalAudits || 0,
        completedThisMonth: completedThisMonth || 0,
        overdueFindings,
        avgScore: Math.round(avgScore),
        complianceScore: Math.round(avgScore),
        openFindings,
      });

      setFindings(allFindings || []);
      setActivities(recentActivities || []);
      setSchedule((scheduledAudits || []).map(s => ({
        ...s,
        programme_name: s.audit_programmes?.programme_name || 'Audit',
        assigned_name: s.users?.full_name || s.users?.email || 'Unassigned',
      })));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <Card key={i} className="h-32 animate-pulse bg-muted/20" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => (
            <Card key={i} className="h-64 animate-pulse bg-muted/20" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Audits"
          value={stats.totalAudits}
          subtitle={`${stats.completedThisMonth} this month`}
          icon={ClipboardCheck}
          color="blue"
        />
        <KPICard
          title="Avg. Score"
          value={`${stats.avgScore}%`}
          subtitle="Across all audits"
          icon={BarChart3}
          color={stats.avgScore >= 85 ? 'green' : stats.avgScore >= 70 ? 'amber' : 'red'}
        />
        <KPICard
          title="Open Findings"
          value={stats.openFindings}
          subtitle={`${stats.overdueFindings} overdue`}
          icon={AlertTriangle}
          color={stats.overdueFindings > 0 ? 'red' : 'amber'}
        />
        <KPICard
          title="Compliance"
          value={`${stats.complianceScore}%`}
          subtitle="ISO 45001 target: 85%"
          icon={ShieldCheck}
          color={stats.complianceScore >= 85 ? 'green' : stats.complianceScore >= 70 ? 'amber' : 'red'}
        />
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          <ComplianceGauge score={stats.complianceScore} />
          <RiskHeatmap findings={findings} />
          <FindingBreakdown findings={findings} />
        </div>

        {/* Right column (1/3) */}
        <div className="space-y-6">
          <UpcomingAudits schedule={schedule} />
          <ActivityFeed activities={activities} />
        </div>
      </div>
    </div>
  );
}
